import React, { useEffect, useRef, useState } from "react";
import { Grid } from "@mui/material";
import { FormButton, SpinnerOverlay } from "components";
import {
  FormikAutocompleteInput,
  FormikCheckboxInput,
  FormikTextInput,
} from "components/UI/Inputs/FormikInputs";
import { Form, Formik } from "formik";
import * as yup from "yup";
import { LARGO, mensajeLargo } from "consts/limites-campos";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { snackbarData, userCredentials } from "recoilState/GlobalState";
import styles from "styles/pages/ModalActions.module.scss";
import {
  CODIGOS_CON_TODOS,
  COMPONENTES_CON_TODOS,
} from "consts/tipos-de-componente";
import { RNHCargoGet } from "services/services-mia/programa-salud/personal/RNHCargoGet";
import { RNHCargoUpd } from "services/services-mia/programa-salud/personal/RNHCargoUpd";

/**
 * Modificación de un cargo: los mismos tres campos del alta.
 */
export default function UpdateModal({ cargo, updateList, closeModal }) {
  const setSnackBarInfo = useSetRecoilState(snackbarData);
  const credentials = useRecoilValue(userCredentials);
  const [data, setData] = useState();
  const [inRequest, setInRequest] = useState(false);
  const formikRef = useRef();

  const getCargoData = async () => {
    setInRequest(true);
    const { data: result } = await RNHCargoGet(credentials, { id: cargo.id });
    const encontrado = Array.isArray(result)
      ? result.find((c) => String(c.rnhCargoId) === String(cargo.id))
      : null;
    setInRequest(false);

    if (!encontrado) {
      setSnackBarInfo({
        message: "No se pudo cargar el cargo seleccionado.",
        severity: "error",
        open: true,
      });
      closeModal();
      return;
    }
    
    formikRef.current.setValues({
      nombre: encontrado.rnhCargoNombre ?? "",
      cod: encontrado.rnhCargoCod ?? "",
      componenteCod: encontrado.rnhTipoComponenteCod ?? cargo.componenteCod ?? "",
      // `?? "N"` y no `?? ""`: el checkbox tiene que arrancar en algun lado, y "N" es a la
      // vez el default correcto y lo que el back espera.
      puedeEditar: encontrado.rnhCargoPuedeEditar ?? "N",
    }, false);
    setData(encontrado);
  };

  const guardarCargo = async (values) => {
    setInRequest(true);
    const response = await RNHCargoUpd(credentials, {
      id: data?.rnhCargoId ?? cargo.id,
      name: values.nombre,
      cod: values.cod,
      componenteCod: values.componenteCod,
      puedeEditar: values.puedeEditar,
      version: data?.rnhCargoVersion ?? cargo.version,
    });
    setInRequest(false);
    if (response.ok) {
      setSnackBarInfo({
        message: "Cargo modificado correctamente",
        severity: "success",
        open: true,
      });
      formikRef.current.resetForm();
      updateList();
      closeModal();
    }
  };

  useEffect(() => {
    getCargoData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{
        nombre: "",
        cod: "",
        componenteCod: "",
        puedeEditar: "N",
      }}
      validationSchema={yup.object({
        nombre: yup
          .string()
          .max(LARGO.nombre, mensajeLargo(LARGO.nombre))
          .required("Debe ingresar un nombre"),
        cod: yup
          .string()
          .max(LARGO.cod, mensajeLargo(LARGO.cod))
          .required("Debe ingresar un código"),
        // Sin `required`: el vacio es la opcion "Todos", no un campo sin completar.
        componenteCod: yup.string().oneOf(CODIGOS_CON_TODOS),
      })}
      onSubmit={(values) => {
        guardarCargo(values);
      }}
    >
      <Form className={styles.inputs} noValidate>
        <div
          className={styles["column"]}
        >
          <Grid
            container
            rowSpacing={0}
            columnSpacing={2}
            sx={{
              width: "100%",
              "& .MuiFormControl-root": { marginTop: 0, marginBottom: 0 },
            }}
          >
            {/* Mismo reparto que el alta, para que los dos modales se lean igual. */}
            <Grid item xs={3}>
              <FormikTextInput
                fullWidth={true}
                name="cod"
                labelText="Código"
                inputProps={{ maxLength: LARGO.cod }}
              />
            </Grid>
            <Grid item xs={5}>
              <FormikTextInput
                fullWidth={true}
                name="nombre"
                labelText="Nombre"
                inputProps={{ maxLength: LARGO.nombre }}
              />
            </Grid>
            <Grid item xs={4}>
              <FormikAutocompleteInput
                name="componenteCod"
                labelText="Repartición"
                options={COMPONENTES_CON_TODOS}
                optionValue="cod"
                optionLabel="label"
                // El vacio no es "falta elegir" sino la opcion "Todos", que viaja
                // como null. Por eso no hay placeholder: siempre hay algo elegido.
              />
            </Grid>
            {/* En su propio renglón y no al lado de los otros tres: no es un dato del
                cargo como el nombre o la repartición, es qué habilita tenerlo. */}
            <Grid item xs={12}>
              <FormikCheckboxInput
                name="puedeEditar"
                labelText="Puede editar"
              />
            </Grid>
          </Grid>
          <FormButton
            inRequest={inRequest}
            newFormData={{ button: "Modificar" }}
            onCloseModal={closeModal}
          />
          {(inRequest) && <SpinnerOverlay />}
        </div>
      </Form>
    </Formik>
  );
}
